import React, { useEffect, useState } from "react";
import orderBy from "lodash/orderBy";
import { useFormikContext } from "formik";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import ImageInput from "../../components/form/ImageInput";
import Input from "../../components/form/Input";
import labels from "../../constants/displayFields";
import Autocomplete from "../../components/form/Autocomplete";
import { getOptions } from "../../utils/form";
import manufacturers from "../../constants/manufacturers";
import DimensionsField from "../../views/capture/DimensionsField";
import colors from "../../constants/colors";
import ImagesKit from "./Images/ImagesKit";
import useAuth from "../../hooks/useAuth";
import { useProject } from "../../components/project/ProjectProvider";
import KitPdfOptionsModal from "../../components/modals/KitPdfOptionsModal";
import AvailableQuantityField from "../../components/modals/InventoryTransactionMultipleModal/quantityCalculation/AvailableQuantityField";

export default function FormFieldsKitting({
  disabledForm,
  asset,
  updateAsset,
  createImage,
  setErrors,
  manufacturersOptions = [],
  colorsOptions = [],
}) {
  const { user } = useAuth();

  // Safely get project context - it might not be available in all contexts (like modals)
  let project = null;
  try {
    const projectContext = useProject();
    project = projectContext.project;
  } catch (error) {
    // useProject is not available in this context, that's okay
    console.warn(
      "ProjectProvider not available in this context, auto-enable feature will not work"
    );
  }

  const { settings } = user.company;
  const { setFieldValue, values } = useFormikContext();
  const [showPdfOptionsModal, setShowPdfOptionsModal] = useState(false);

  useEffect(() => {
    if (!values.itemCode && asset.itemCode) {
      setFieldValue("itemCode", asset.itemCode);
    }
  }, [asset.itemCode, setFieldValue, values.itemCode]);

  // Default groupBySkuNumber for new kits based on project settings
  useEffect(() => {
    if (!asset.id && values.groupBySkuNumber === undefined) {
      setFieldValue("groupBySkuNumber", !!(project?.kitting && project?.referenceAssets));
    }
  }, [project?.kitting, project?.referenceAssets, asset.id, values.groupBySkuNumber, setFieldValue]);

  const handleUpdateAssetField = field => (value, { onSuccess, onFailure } = {}) => {
    if (field === "barcode") {
      setFieldValue(field, value);
    }
    updateAsset({
      variables: { id: asset.id, input: { [field]: value } },
      onSuccess,
      onFailure: errors => {
        setErrors(errors);
        onFailure && onFailure(errors);
      },
    });
  };

  const handleCreateImage = (file, { onSuccess, onFailure } = {}) => {
    return createImage({
      variables: { assetId: asset.id, image: file },
      onSuccess,
      onFailure,
    });
  };

  return (
    <Card>
      <CardContent>
        <Box mt={-1} mb={1}>
          <Typography fontWeight={500} fontSize={18}>
            Kit Info
          </Typography>
        </Box>
        <Grid container alignItems="flex-start" spacing={1}>
          <Grid container spacing={1} size={{ md: 6, xs: 12 }}>
            <Grid size={12}>
              <ImagesKit disabled={disabledForm} asset={asset} />
            </Grid>
            {!disabledForm && (
              <Grid size={12}>
                <ImageInput imageUrl={asset.photo} onUpload={handleCreateImage} allowLibrary />
              </Grid>
            )}
          </Grid>
          <Grid container spacing={1} size={{ md: 6, xs: 12 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label={settings.filter_field1}
                value={asset.client.name}
                sx={{
                  fieldset: { borderColor: "primary.main" },
                  label: { color: "primary.main" },
                  input: { color: "primary.main" },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label={settings.filter_field2}
                value={asset.project.name}
                sx={{
                  fieldset: { borderColor: "primary.main" },
                  label: { color: "primary.main" },
                  input: { color: "primary.main" },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField size="small" label="Kit ID" disabled value={asset.itemId} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Input
                fullWidth
                name="itemCode"
                label="Kit Code"
                disabled={disabledForm}
                type="autoExpand"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                fullWidth
                disabled={disabledForm}
                saveCustomOption
                name="manufacturer"
                label={labels.manufacturer}
                options={orderBy(getOptions(manufacturers, manufacturersOptions), v =>
                  v.value?.toLowerCase()
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Input disabled={disabledForm} name="model" label={labels.model} type="autoExpand" />
            </Grid>
            <Grid size={12}>
              <Input
                type="autoExpand"
                fullWidth
                name="description"
                label={labels.description}
                disabled={disabledForm}
              />
            </Grid>
            <Grid size={12}>
              <Input
                type="autoExpand"
                fullWidth
                name="descriptionComments"
                label="Comments"
                disabled={disabledForm}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DimensionsField
                name="dimensions"
                disabled={disabledForm}
                asset={asset}
                handleUpdateAssetField={handleUpdateAssetField}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                saveCustomOption
                name="color"
                label={labels.color}
                options={orderBy(getOptions(colors, colorsOptions), v => v.value)}
                disabled={disabledForm}
              />
            </Grid>
            <Grid size={12}>
              <AvailableQuantityField
                asset={asset}
                label="Kit Quantity"
                queryParamName="showKitQuantityCalculation"
                selectionParamName="kitDetailsId"
                selectionParamValue={asset.id}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                size="small"
                label="Quantity in Warehouse"
                disabled
                value={asset.warehouseQuantity || 0}
                fullWidth
              />
            </Grid>
            {settings.reservations && (
              <Grid size={12}>
                <TextField
                  size="small"
                  label="Reserved Quantity"
                  disabled
                  value={asset.reservedQuantity}
                  fullWidth
                />
              </Grid>
            )}
            {asset?.id && (
              <Grid size={12}>
                <Grid container spacing={1}>
                  <Grid size={12}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setShowPdfOptionsModal(true)}
                      fullWidth
                    >
                      Generate Kit PDF
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {project?.kitting && (
              <Grid size={12}>
                <Box>
                  <Box
                    sx={{
                      px: 1,
                      display: "flex",
                      justifyContent: "center",
                      height: 36.5,
                      borderRadius: "4px",
                      border: "1px solid rgba(0, 0, 0, 0.23)",
                      "& label": {
                        display: "flex",
                        flexDirection: "row-reverse",
                        justifyContent: "center",
                        mx: 0,
                        height: 36.5,
                        "& > span > p": {
                          lineHeight: 1,
                          color: "text.primary",
                        },
                      },
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.groupBySkuNumber || false}
                          onChange={e => {
                            const newValue = e.target.checked;
                            setFieldValue("groupBySkuNumber", newValue);
                            handleUpdateAssetField("groupBySkuNumber")(newValue);
                          }}
                          disabled={disabledForm}
                          sx={{ p: 1 }}
                        />
                      }
                      label={
                        <Typography variant="body2" color="textPrimary">
                          Group by SKU Number
                        </Typography>
                      }
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    When enabled, kit quantities will be calculated using all assets with the same
                    SKU number and description.
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      </CardContent>
      {showPdfOptionsModal && (
        <KitPdfOptionsModal kitId={asset.id} onClose={() => setShowPdfOptionsModal(false)} />
      )}
    </Card>
  );
}
